/**
 * Color Palette Configuration
 * Green color scheme based on dietedics logo colors
 */

export const colors = {
  // Primary Colors - Green tones from logo
  primary: {
    light: '#9FB0A2',      // Light green (lighter version of logo green)
    main: '#718a73',       // Medium green (from logo)
    dark: '#3a513c',       // Dark green (from logo)
    accent: '#5A6F5C'      // Medium-dark green accent
  },

  // Secondary Colors - Complementary green tones
  secondary: {
    light: '#B8D4C0',      // Soft sage green
    main: '#8AB89A',       // Medium sage green
    dark: '#6A987A',       // Deeper sage green
    accent: '#A5C4B0'      // Light sage accent
  },

  // Background Colors
  background: {
    light: '#F8FAF9',      // Very light green-white
    main: '#F5F7F6',       // Soft off-white with green tint
    dark: '#F0F2F1'        // Light gray-green
  },

  // Text Colors
  text: {
    primary: '#2A3A2C',    // Dark green-gray
    secondary: '#4A5A4C',  // Medium green-gray
    light: '#6B7A6D',      // Light green-gray
    inverse: '#FFFFFF'     // White for dark backgrounds
  },

  // Surface Colors (cards, panels)
  surface: {
    light: '#FFFFFF',      // Pure white
    elevated: '#FEFEFE',  // Slightly off-white
    dark: '#F5F7F6'       // Light gray-green
  },

  // Border Colors
  border: {
    light: '#E0E8E2',     // Very light gray-green
    main: '#D0D8D2',      // Light gray-green
    dark: '#B8C4BA'       // Medium gray-green
  },

  // Status Colors
  success: {
    light: '#C8E6D1',     // Soft green
    main: '#81C784',      // Medium green
    dark: '#66BB6A'       // Deeper green
  },
  error: {
    light: '#F8D7DA',     // Soft red
    main: '#E57373',      // Medium red
    dark: '#D32F2F'       // Deeper red
  },
  warning: {
    light: '#FFF3CD',     // Soft yellow
    main: '#FFE5A5',      // Medium yellow
    dark: '#E8D48A'       // Deeper yellow
  },
  info: {
    light: '#D1ECF1',     // Soft blue
    main: '#B8D4E8',      // Medium blue
    dark: '#A5C4D4'       // Deeper blue
  }
}

// Dark Mode Colors
export const darkColors = {
  // Primary Colors - Muted green versions for dark mode
  primary: {
    light: '#8FA092',      // Medium green
    main: '#7A927D',       // Slightly muted green
    dark: '#5A6F5C',       // Deeper green
    accent: '#6A7F6C'      // Muted green accent
  },

  // Secondary Colors
  secondary: {
    light: '#9AB8A4',      // Medium sage green
    main: '#8AA894',       // Slightly muted sage
    dark: '#7A9884',       // Deeper sage
    accent: '#A0B8A8'      // Light sage accent
  },

  // Background Colors - Dark grays with green undertones
  background: {
    light: '#1F2520',      // Dark green-gray
    main: '#1A1F1B',       // Very dark green-gray
    dark: '#151815'        // Almost black with green tone
  },

  // Text Colors
  text: {
    primary: '#E0E8E2',    // Light green-gray
    secondary: '#C8D4CA', // Medium green-gray
    light: '#A8B4AA',     // Muted green-gray
    inverse: '#1A1F1B'    // Dark for light backgrounds
  },

  // Surface Colors
  surface: {
    light: '#252A26',      // Dark green-gray
    elevated: '#2A2F2B',   // Slightly lighter dark green-gray
    dark: '#202520'        // Very dark green-gray
  },

  // Border Colors
  border: {
    light: '#3A403C',      // Medium dark green-gray
    main: '#4A504C',       // Lighter dark green-gray
    dark: '#5A605C'        // Even lighter
  },

  // Status colors adjusted for dark mode
  success: {
    light: '#7AA88A',      // Muted green
    main: '#6A987A',       // Medium green
    dark: '#5A886A'        // Deeper green
  },
  error: {
    light: '#E57373',      // Muted red
    main: '#D32F2F',       // Medium red
    dark: '#C21818'        // Deeper red
  },
  warning: {
    light: '#E8D48A',      // Muted yellow
    main: '#D4C48A',       // Medium yellow
    dark: '#C4B48A'        // Deeper yellow
  },
  info: {
    light: '#A5C4D4',      // Muted blue
    main: '#9AB4C4',       // Medium blue
    dark: '#8AA4B4'        // Deeper blue
  }
}

// Colorblind-Accessible Colors (Light Mode)
export const colorblindColors = {
  // Primary Colors - Blue tones (accessible for colorblind users)
  primary: {
    light: '#A8C5E0',      // Light blue
    main: '#6B9BD1',       // Medium blue
    dark: '#4A7BA8',       // Darker blue
    accent: '#8B9DC3'       // Blue-gray
  },

  // Secondary Colors - Orange/amber tones (high contrast with blue)
  secondary: {
    light: '#F4C2A1',      // Light orange
    main: '#E8A87C',       // Medium orange
    dark: '#D4945A',       // Deeper orange
    accent: '#F5B896'      // Soft orange
  },

  // Background Colors
  background: {
    light: '#F8FAF9',      // Very light green-white
    main: '#F5F7F6',       // Soft off-white with green tint
    dark: '#F0F2F1'        // Light gray-green
  },

  // Text Colors
  text: {
    primary: '#2C3E50',    // Darker gray for better contrast
    secondary: '#5A6C7D',  // Medium gray
    light: '#8A9BA8',      // Light gray
    inverse: '#FFFFFF'     // White for dark backgrounds
  },

  // Surface Colors
  surface: {
    light: '#FFFFFF',      // Pure white
    elevated: '#FEFEFE',  // Slightly off-white
    dark: '#F5F7F6'       // Light gray-green
  },

  // Border Colors
  border: {
    light: '#D1D9E0',     // Light blue-gray
    main: '#B8C5D1',      // Medium blue-gray
    dark: '#9FAFBF'       // Darker blue-gray
  },

  // Status Colors - High contrast, colorblind-friendly
  success: {
    light: '#C8E6C9',     // Light green (distinguishable)
    main: '#81C784',      // Medium green
    dark: '#66BB6A'       // Deeper green
  },
  error: {
    light: '#FFCDD2',     // Light red-pink
    main: '#E57373',      // Medium red
    dark: '#EF5350'       // Deeper red
  },
  warning: {
    light: '#FFF9C4',     // Light yellow
    main: '#FFD54F',      // Medium amber
    dark: '#FFC107'       // Deeper amber
  },
  info: {
    light: '#B3E5FC',     // Light blue
    main: '#64B5F6',      // Medium blue
    dark: '#42A5F5'       // Deeper blue
  }
}

// Colorblind-Accessible Colors (Dark Mode)
export const colorblindDarkColors = {
  // Primary Colors
  primary: {
    light: '#6B9BD1',      // Medium blue
    main: '#5A8BC4',       // Slightly darker blue
    dark: '#4A7BA8',       // Darker blue
    accent: '#7A9BC8'      // Blue-gray
  },

  // Secondary Colors
  secondary: {
    light: '#E8A87C',      // Medium orange
    main: '#D4945A',       // Slightly darker orange
    dark: '#C0803A',       // Deeper orange
    accent: '#E0A068'      // Soft orange
  },

  // Background Colors
  background: {
    light: '#1F2520',      // Dark green-gray
    main: '#1A1F1B',       // Very dark green-gray
    dark: '#151815'        // Almost black with green tone
  },

  // Text Colors
  text: {
    primary: '#E8E8E8',    // Light gray
    secondary: '#C8C8C8',  // Medium gray
    light: '#A8A8A8',     // Muted gray
    inverse: '#1F1B1B'    // Dark for light backgrounds
  },

  // Surface Colors
  surface: {
    light: '#252A26',      // Dark green-gray
    elevated: '#2A2F2B',   // Slightly lighter dark green-gray
    dark: '#202520'        // Very dark green-gray
  },

  // Border Colors
  border: {
    light: '#4A5055',      // Medium dark gray
    main: '#5A6065',       // Lighter dark gray
    dark: '#6A7075'        // Even lighter
  },

  // Status colors - colorblind-friendly
  success: {
    light: '#66BB6A',      // Green
    main: '#4CAF50',       // Medium green
    dark: '#388E3C'        // Deeper green
  },
  error: {
    light: '#EF5350',      // Red
    main: '#E53935',       // Medium red
    dark: '#C62828'        // Deeper red
  },
  warning: {
    light: '#FFC107',      // Amber
    main: '#FFB300',       // Medium amber
    dark: '#FFA000'        // Deeper amber
  },
  info: {
    light: '#42A5F5',      // Blue
    main: '#2196F3',       // Medium blue
    dark: '#1976D2'        // Deeper blue
  }
}

